/**
 * Which products can carry a discount, by pricing model.
 *
 * An ALLOWLIST, because `discountedPrice` is only ever consulted by the STANDARD
 * branch of the server's resolveItemPriceAud. Every other model prices from its
 * own field and never looks at it: tiered from the chosen tier, subscription
 * from `subscriptionPrice`, pay-what-you-want from the buyer's amount, free from
 * nothing at all.
 *
 * So a discount on one of those was a sale that did not exist. It was stored,
 * swept live, stamped the `discounted` tag onto the product (which put it in
 * discount filters), and fired the seller's "Discount Live" notification — while
 * every buyer went on being charged full price. Subscription was the reachable
 * case: the old check named only free and pwyw, and both product forms write
 * `price = subscriptionPrice` on a subscription listing, so the server's base
 * price was non-zero and the discount went through. Tiered was refused only by
 * accident, with a message about the price being zero rather than about the
 * model.
 *
 * A missing / null model is a legacy standard product — the server falls through
 * to its standard branch for those — so it is discountable.
 *
 * THE BINDING CHECK IS createDiscount (functions/src/lib/discounts.ts,
 * isDiscountablePricingModel). This exists so the Discount action is not offered
 * for a product the server will refuse; keep the two in step.
 */

export function isDiscountablePricingModel(pricingModel?: string | null): boolean {
  return pricingModel === undefined ||
    pricingModel === null ||
    pricingModel === 'standard';
}

/** Whether this product can be offered a new discount, ignoring existing ones. */
export function isDiscountableProduct(
  product: { pricingModel?: string | null } | null | undefined,
): boolean {
  return !!product && isDiscountablePricingModel(product.pricingModel);
}
